"use client";

import Link from "next/link";
import { useState } from "react";
import { ChevronDown, Calendar, ClipboardList } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { allServices } from "@/data/Static/services";

export default function DesktopNav() {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <nav className="hidden xl:flex items-center gap-2 2xl:gap-4">
      <Link href="/" className="px-2 py-3 text-hill-primary hover:text-hill-secondary font-semibold text-[13px] uppercase tracking-[0.12em] transition-all">
        Főoldal
      </Link>
      <Link href="/rolunk" className="px-2 py-3 text-hill-primary hover:text-hill-secondary font-semibold text-[13px] uppercase tracking-[0.12em] transition-all">
        Rólunk
      </Link>
      
      {/* Services Mega Menu */}
      <div 
        className="relative group"
        onMouseEnter={() => setIsServicesOpen(true)}
        onMouseLeave={() => setIsServicesOpen(false)}
      >
        <button className="px-2 py-3 flex items-center gap-1.5 text-hill-primary group-hover:text-hill-secondary font-semibold text-[13px] uppercase tracking-[0.12em] transition-all">
          Szolgáltatások
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isServicesOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: 10, x: "-50%" }}
              transition={{ duration: 0.2 }}
              className="absolute left-1/2 top-full pt-4 z-50"
            >
              <div className="bg-white border border-border shadow-2xl rounded-2xl p-6 w-[880px] grid grid-cols-3 gap-y-4 gap-x-8 backdrop-blur-md bg-white/95">
                {allServices.map((service) => (
                  <Link 
                    key={service.id} 
                    href={service.href}
                    className="group/item flex items-start gap-4 p-3 rounded-xl hover:bg-hill-primary/5 transition-all"
                  >
                    <div className={`w-10 h-10 p-2 rounded-lg flex items-center justify-center transition-all ${service.bgColor} ${service.color} group-hover/item:scale-110`}>
                      {service.icon}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-bold text-[11px] uppercase tracking-widest text-hill-primary group-hover/item:text-hill-secondary transition-colors">
                        {service.title}
                      </span>
                      <span className="text-[10px] text-muted-foreground leading-relaxed line-clamp-2 font-karla">
                        {service.description}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Link href="/arlista" className="px-2 py-3 text-hill-primary hover:text-hill-secondary font-semibold text-[13px] uppercase tracking-[0.12em] transition-all">
        Árlista
      </Link>
      
      <div className="h-4 w-px bg-hill-primary/20 mx-1" />

      {/* Booking Dropdown */}
      <div 
        className="relative group"
        onMouseEnter={() => setIsBookingOpen(true)}
        onMouseLeave={() => setIsBookingOpen(false)}
      >
        <button className="px-2 py-3 flex items-center gap-1.5 text-hill-primary group-hover:text-hill-secondary font-semibold text-[13px] uppercase tracking-[0.12em] transition-all">
          Foglalás
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isBookingOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isBookingOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 top-full pt-4 z-50"
            >
              <div className="bg-white border border-border shadow-2xl rounded-2xl p-3 w-72 flex flex-col gap-1 backdrop-blur-md bg-white/95">
                <Link 
                  href="/kapcsolatfelvetel"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-hill-primary/5 transition-all group/sub"
                >
                  <div className="w-8 h-8 rounded-lg bg-hill-secondary/10 flex items-center justify-center text-hill-secondary group-hover/sub:bg-hill-secondary group-hover/sub:text-white transition-all">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-xs text-hill-primary">Fogászati foglalás</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Vegye fel a kapcsolatot</span>
                  </div>
                </Link>
                <Link 
                  href="/logopedia-foglalas"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-hill-primary/5 transition-all group/sub"
                >
                  <div className="w-8 h-8 rounded-lg bg-hill-primary/10 flex items-center justify-center text-hill-primary group-hover/sub:bg-hill-primary group-hover/sub:text-white transition-all">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-xs text-hill-primary">Logopédia Foglalás</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Időpont foglalása</span>
                  </div>
                </Link>
                <Link
                  href="/gyogytorna-foglalas"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-hill-primary/5 transition-all group/sub"
                >
                  <div className="w-8 h-8 rounded-lg bg-hill-primary/10 flex items-center justify-center text-hill-primary group-hover/sub:bg-hill-primary group-hover/sub:text-white transition-all">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-xs text-hill-primary">Gyógytorna foglalás</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Időpont foglalása</span>
                  </div>
                </Link>
                <Link 
                  href="/logopedia-foglalas/manage"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-hill-primary/5 transition-all group/sub"
                >
                  <div className="w-8 h-8 rounded-lg bg-hill-primary/10 flex items-center justify-center text-hill-primary group-hover/sub:bg-hill-primary group-hover/sub:text-white transition-all">
                    <ClipboardList className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-xs text-hill-primary">Foglalásaim</span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Foglalások kezelése</span>
                  </div>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
